import { getDoc, doc, setDoc, collection } from 'firebase/firestore'

import { Car, FirebaseCollections, FirebaseFields, Timestamp } from 'src/types'
import { db } from 'src/configuration-firebase'

import FirestoreServiceInstance, {
  FirebaseFirestoreService,
} from 'src/api/service'
import localService from 'src/api/localeService'
import { UserInitializeService } from 'src/api/index'

export class CarService extends UserInitializeService {
  constructor(private readonly firestoreService: FirebaseFirestoreService) {
    super()
  }

  private async getCarsData() {
    const collectionName = await this.getUserCollectionName()
    const carDocRef = doc(db, collectionName, FirebaseCollections.CARS)
    const carSnap = await getDoc(carDocRef)

    return { carSnap, carDocRef }
  }

  async getUserCars(): Promise<Car<Timestamp>[]> {
    const { carSnap } = await this.getCarsData()

    if (carSnap.exists()) {
      const data = carSnap.data()

      return data[FirebaseFields.LIST_OF_CARS] || []
    }

    return []
  }

  async addUserCar(car: Car): Promise<void> {
    const usersCars = await this.getUserCars()
    const { carDocRef } = await this.getCarsData()

    if (
      usersCars.some(existingCar => existingCar.carNumber === car.carNumber)
    ) {
      throw new Error(
        `${localService.translation.fireBaseErrors.carExist.start}: "${car.carNumber}" ${localService.translation.fireBaseErrors.carExist.endAlready}`,
      )
    }

    if (!usersCars.length) {
      await setDoc(carDocRef, {
        [FirebaseFields.LIST_OF_CARS]: [{ ...car, isActive: true }],
      })
    }

    const updatedCars = car.isActive
      ? usersCars.map(existingCar => ({ ...existingCar, isActive: false }))
      : [...usersCars]

    const collectionRef = collection(db, FirebaseCollections.CARS)
    const newItemId = doc(collectionRef).id + crypto.randomUUID()

    const newDataWithId = { id: newItemId, ...car }

    const finalUpdatedCars = [newDataWithId, ...updatedCars]

    await setDoc(carDocRef, { [FirebaseFields.LIST_OF_CARS]: finalUpdatedCars })
  }

  async setActiveCar(carNumber: string): Promise<void> {
    const usersCars = await this.getUserCars()
    const { carDocRef } = await this.getCarsData()

    const carExists = usersCars.some(car => car.carNumber === carNumber)

    if (!carExists) {
      throw new Error(
        `${localService.translation.fireBaseErrors.carExist.start}: "${carNumber}" ${localService.translation.fireBaseErrors.carExist.endNot}`,
      )
    }

    const updatedCars = usersCars.map(car => ({
      ...car,
      isActive: car.carNumber === carNumber,
    }))

    await setDoc(carDocRef, { [FirebaseFields.LIST_OF_CARS]: updatedCars })
  }

  async updateDataUserCar(carNumber: string, newData: Car): Promise<void> {
    const usersCars = await this.getUserCars()
    const { carDocRef } = await this.getCarsData()

    if (!usersCars.some(car => car.carNumber === carNumber)) {
      throw new Error(
        `${localService.translation.fireBaseErrors.carExist.start}: "${carNumber}" ${localService.translation.fireBaseErrors.carExist.endNot}`,
      )
    }

    if (
      usersCars.some(
        car =>
          car.carNumber === newData.carNumber &&
          carNumber !== newData.carNumber,
      )
    ) {
      throw new Error(
        `${localService.translation.fireBaseErrors.carExist.start}: "${carNumber}" ${localService.translation.fireBaseErrors.carExist.endAlready}`,
      )
    }

    const updatedData = usersCars.map(car => {
      if (car.carNumber === carNumber) {
        return { ...newData, id: car.id }
      }

      return newData.isActive ? { ...car, isActive: false } : car
    })

    await setDoc(carDocRef, { [FirebaseFields.LIST_OF_CARS]: updatedData })
  }

  async deleteUserCar(carNumber: string): Promise<string> {
    const usersCars = await this.getUserCars()
    const { carDocRef } = await this.getCarsData()

    if (usersCars.length === 1 && usersCars[0].carNumber === carNumber) {
      throw new Error(localService.translation.fireBaseErrors.lastCar)
    }

    const carIndex = usersCars.findIndex(car => car.carNumber === carNumber)

    if (carIndex === -1) {
      throw new Error(
        `${localService.translation.fireBaseErrors.carExist.start}: "${carNumber}" ${localService.translation.fireBaseErrors.carExist.endNot}`,
      )
    }

    const isActiveDeleted = usersCars[carIndex].isActive

    const updatedCars = usersCars.filter(car => car.carNumber !== carNumber)

    if (isActiveDeleted && updatedCars.length > 0) {
      let newActiveIndex = carIndex

      if (newActiveIndex >= updatedCars.length) {
        newActiveIndex = updatedCars.length - 1
      }

      updatedCars.forEach(car => (car.isActive = false))
      updatedCars[newActiveIndex].isActive = true
    }

    await this.firestoreService.deleteAllInfoCar(carNumber)

    await setDoc(carDocRef, { [FirebaseFields.LIST_OF_CARS]: updatedCars })

    return carNumber
  }
}

export default new CarService(FirestoreServiceInstance)
